import { queryOptions, useQuery } from "@tanstack/react-query";
import { AutocompleteField, type SelectFieldOption } from "@wayflyer/flyui";
import { useMemo, useState } from "react";
import { debounce } from "lodash";

/**
 * A generic interface for return types of async calls to IndustryClassifier lib
 */
export type IndustrySearchResult = { code: string; activity: string; subText?: string; relevanceScore?: number };

/**
 * Async function to provide industry suggestions based on a search term (bring-your-own-backend)
 * @example
 * ```typescript
 * (params) => apiClient.get(`/api/industry-search?query=${params.query}`).then(({ data }) => data)
 * ```
 */
export type GetSuggestedIndustries = (params: { query: string }) => Promise<IndustrySearchResult[]>;

/**
 * Async function to fetch industry info from a known industry code
 * (e.g. for rendering the component inside a pre-filled form)
 * @example
 * ```typescript
 * (params) => apiClient.get(`/api/industry-lookup/${params.code}/`).then(({ data }) => data)
 * ```
 */
export type GetIndustryByCode = (params: { code: string }) => Promise<IndustrySearchResult>;

interface Props {
  industryCode: string | undefined;
  onChangeIndustryCode: (code: string) => void;
  /** Optional, industry name to display in the input when the component is loaded */
  industryName?: string;
  /** Optional, triggers the callback with an updated industry activity */
  onChangeIndustryName?: (name: string) => void;
  fieldName: string;
  disabled?: boolean;
  label?: string;
  placeholder?: string;
  error?: string;
  required?: boolean;
  helperText?: string;
  getSuggestedIndustries: GetSuggestedIndustries;
  getIndustryByCode?: GetIndustryByCode;
}

export const IndustryAutocompleteField = ({
  industryCode,
  onChangeIndustryCode,
  industryName,
  onChangeIndustryName,
  fieldName,
  disabled = false,
  label = "Industry Classification",
  placeholder,
  error,
  required = false,
  helperText,
  getSuggestedIndustries,
  getIndustryByCode,
}: Props) => {
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSetSearchTerm = useMemo(() => debounce(setSearchTerm, 400, { leading: true }), []);

  const {
    data: suggestedIndustries,
    isFetching,
    error: searchError,
  } = useQuery(
    queryOptions<IndustrySearchResult[]>({
      enabled: Boolean(searchTerm?.trim()),
      queryKey: ["industry-search", searchTerm],
      queryFn: () => getSuggestedIndustries({ query: searchTerm }),
    })
  );

  // Fetch industry when loading with an existing industry code
  const { data: currentlySelectedIndustry } = useQuery(
    queryOptions<IndustrySearchResult | void>({
      enabled: Boolean(industryCode) && Boolean(getIndustryByCode),
      queryKey: ["industry-by-code", industryCode],
      queryFn: () => getIndustryByCode?.({ code: industryCode ?? "" }),
    })
  );

  const options = useMemo<SelectFieldOption[]>(() => {
    const suggestedOptions: SelectFieldOption[] =
      suggestedIndustries?.map((industry) => ({
        id: industry.code,
        text: industry.activity,
        subText: industry.subText ?? undefined,
      })) ?? [];

    if (!currentlySelectedIndustry || suggestedOptions.some((opt) => opt.id === currentlySelectedIndustry.code)) {
      return suggestedOptions;
    }

    return [
      {
        id: currentlySelectedIndustry.code,
        text: currentlySelectedIndustry.activity,
        subText: currentlySelectedIndustry.subText ?? undefined,
      },
      ...suggestedOptions,
    ];
  }, [suggestedIndustries, currentlySelectedIndustry]);

  const handleChange = (selectedCode: string | null) => {
    if (selectedCode) {
      const selectedOption = options.find((option) => option.id === selectedCode);
      if (selectedOption) {
        onChangeIndustryCode(selectedOption.id);
        onChangeIndustryName?.(selectedOption.text);
      }
    } else {
      onChangeIndustryCode("");
      onChangeIndustryName?.("");
    }
  };

  return (
    <AutocompleteField
      name={fieldName}
      required={required}
      label={label}
      placeholder={placeholder}
      clearable={disabled ? undefined : true}
      error={error}
      onSearchTermChange={debouncedSetSearchTerm}
      fetching={isFetching && !searchError}
      disabled={disabled}
      externalOptions
      onChange={handleChange}
      controlledValue={industryCode ?? null}
      initialSearchTerm={industryName}
      options={options}
      helperText={helperText}
    />
  );
};
